import { FINANCIAL_COLORS } from '@components/charts/chartConstants';
import type {
  DisplayLine,
  MatrixTableRow,
  PerformanceTrendsData,
  ValueTone,
} from '@app-types/performanceTrends';

// Mock data for the Performance Trends screen (PT01).
// Headers and row labels are taken verbatim from Figma via the Dev Mode MCP.
// Many numeric cells in Figma are placeholder/dummy values; where exact
// per-cell mapping was ambiguous it is marked TODO(data-check). The component
// APIs stay stable so real API values can replace these later.

// ---- small helpers --------------------------------------------------
const L = (text: string, tone: ValueTone = 'default'): DisplayLine => ({ text, tone });

// Keeps dynamic unit labels such as `単位:百万ドル` visible on fixed-layout
// matrix tables while the existing wrapper continues to handle mobile scrolling.
const UNIT_LABEL_COLUMN_WIDTH = 104;

/** YoY line: red for positive, blue for negative, muted for placeholders. */
const yoy = (s: string): DisplayLine => {
  if (s === '--' || s === '---' || s === '') return L(s, 'default');
  return L(s, s.includes('-') ? 'negative' : 'positive');
};

/** A value cell with an optional YoY second line. */
const cell = (value: string, yoyStr?: string): DisplayLine[] =>
  yoyStr === undefined ? [L(value)] : [L(value), yoy(yoyStr)];

/** A date-based row: label column shows year + (前年比). */
const trendRow = (
  key: string,
  year: string,
  cells: DisplayLine[][],
  showYoyLabel = true
): MatrixTableRow => ({
  key,
  label: showYoyLabel ? [L(year), L('(前年比)')] : [L(year)],
  cells,
});

const ANNUAL_YEARS = [
  '2025/03予',
  '2024/03',
  '2023/03',
  '2022/03',
  '2021/03',
  '2020/03',
  '2019/03',
  '2018/03',
  '2017/03',
  '2016/03',
];

const ANNUAL_NOTE =
  '※前年比は各項目の前年通期決算の値から算出し表示。\n' +
  '※「予」は予想決算';

const QUARTERLY_NOTE =
  '※前年比は各項目の前年同月期四半期の会計期間（3ヶ月）の値から算出し表示。\n' +
  '※「予」は予想決算';

const forecastControls = {
  forecastLabel: '予想種別',
  forecastOptions: [
    { value: 'consensus', label: 'コンセンサス' },
    { value: 'company', label: '会社予想' },
  ],
  selectedForecast: 'consensus',
  periodLabel: '表示期間',
};

const annualControls = () => ({
  ...forecastControls,
  forecastOptions: forecastControls.forecastOptions.map((option) => ({ ...option })),
  periodOptions: [
    { value: '10', label: '直近10期' },
    { value: '5', label: '直近5期' },
  ],
  selectedPeriod: '10',
});

const quarterlyControls = () => ({
  ...forecastControls,
  forecastOptions: forecastControls.forecastOptions.map((option) => ({ ...option })),
  periodOptions: [
    { value: '3', label: '直近3年' },
    { value: '5', label: '直近5年' },
    { value: '10', label: '直近10年' },
  ],
  selectedPeriod: '3',
});

// ---- Annual performance (values are Figma placeholders) -------------
const annualRows: MatrixTableRow[] = ANNUAL_YEARS.map((year, i) =>
  trendRow(`annual-${i}`, year, [
    cell('49,500,000', '(+2.0％)'),
    cell('49,500,000', '(+2.0％)'),
    cell('49,500,000', '(+2.0％)'),
    cell('49,500,000', '(+2.0％)'),
    cell('224.8', '--'),
    cell('2,820.0', '---'),
  ])
);

// ---- Quarterly performance (real values where extracted) ------------
// TODO(data-check): rows 2017/03–2021/03 use representative values.
const quarterlyRows: MatrixTableRow[] = [
  trendRow('q-2016', '2016/03/31', [
    cell('8,998,661.00', '(+8.94)'),
    cell('956,958.00', '(+11.48)'),
    cell('997,965.00', '(+24.83)'),
    cell('1,043,896.00', '(+67.76)'),
    cell('181.82', '(+83.40)'),
    cell('10.00', '(+25.00)'),
    cell('2016/05/28'),
  ]),
  trendRow('q-2017', '2017/03/31', [
    cell('9,210,000.00', '(+2.35)'),
    cell('1,010,000.00', '(+5.54)'),
    cell('1,050,000.00', '(+5.22)'),
    cell('930,000.00', '(-10.91)'),
    cell('162.00', '(-10.90)'),
    cell('11.00', '(+10.00)'),
    cell('2017/05/28'),
  ]),
  trendRow('q-2018', '2018/03/31', [
    cell('9,560,000.00', '(+3.80)'),
    cell('1,060,000.00', '(+4.95)'),
    cell('1,090,000.00', '(+3.81)'),
    cell('870,000.00', '(-6.45)'),
    cell('151.50', '(-6.48)'),
    cell('11.50', '(+4.55)'),
    cell('2018/05/28'),
  ]),
  trendRow('q-2019', '2019/03/31', [
    cell('9,700,000.00', '(+1.46)'),
    cell('1,080,000.00', '(+1.89)'),
    cell('1,100,000.00', '(+0.92)'),
    cell('900,000.00', '(+3.45)'),
    cell('156.00', '(+2.97)'),
    cell('11.50', '(0.00)'),
    cell('2019/05/28'),
  ]),
  trendRow('q-2020', '2020/03/31', [
    cell('9,800,000.00', '(+1.03)'),
    cell('1,090,000.00', '(+0.93)'),
    cell('1,110,000.00', '(+0.91)'),
    cell('910,000.00', '(+1.11)'),
    cell('158.00', '(+1.28)'),
    cell('11.50', '(0.00)'),
    cell('2020/05/28'),
  ]),
  trendRow('q-2021', '2021/03/31', [
    cell('9,000,000.00', '(-8.16)'),
    cell('1,000,000.00', '(-8.26)'),
    cell('1,020,000.00', '(-8.11)'),
    cell('820,000.00', '(-9.89)'),
    cell('142.00', '(-10.13)'),
    cell('11.50', '(0.00)'),
    cell('2021/05/28'),
  ]),
  trendRow('q-2022', '2022/03/31', [
    cell('9,921,513.00', '(+10.26)'),
    cell('1,112,396.00', '(+16.24)'),
    cell('1,117,503.00', '(+11.98)'),
    cell('888,406.00', '(-14.90)'),
    cell('100.65', '(-44.64)'),
    cell('12.00', '(+20.00)'),
    cell('2022/05/28'),
  ]),
  trendRow('q-2023', '2023/03/31', [
    cell('10,974,373.00', '(+10.61)'),
    cell('1,273,205.00', '(+14.46)'),
    cell('1,274,496.00', '(+14.05)'),
    cell('1,011,773.00', '(+13.89)'),
    cell('175.98', '(+74.84)'),
    cell('14.0', '(+16.67)'),
    cell('2023/05/28'),
  ]),
  trendRow('q-2024', '2024/03/31', [
    cell('13,020,768.00', '(+18.65)'),
    cell('1,166,986.00', '(-8.34)'),
    cell('1,268,662.00', '(-0.46)'),
    cell('980,494.00', '(-3.09)'),
    cell('202.61', '(+15.13)'),
    cell('16.00', '(+14.29)'),
    cell('2024/05/28'),
  ]),
  trendRow('q-2025', '2025/03/31', [
    cell('12,957,064.00', '(-0.49)'),
    cell('1,412,640.00', '(+21.05)'),
    cell('1,473,726.00', '(+16.16)'),
    cell('1,159,887.00', '(+18.30)'),
    cell('155.55', '(-23.23)'),
    cell('19.00', '(+18.75)'),
    cell('2025/05/28'),
  ]),
];

// ---- Balance sheet -------------------------------------------------
// Node 3349:24052. Rows/columns are built from the API response by
// `mapBalanceSheetApiToFinancialSection`, so no mock financial values live here.

// ---- Cash flow (real values, node 3347:21870) -----------------------
// TODO(data-check): 投資CF for 2024/03 was not captured; representative value.
const cashFlowRows: MatrixTableRow[] = [
  trendRow('cf-2021', '2021/03/31', [
    cell('27,507,843.00', '(+19.40)'),
    cell('-563,910.00', '(+58.30)'),
    cell('-338,533.00', '(-615.60)'),
    cell('16,488.12', '(-1.92)'),
    cell('2021/05/28'),
  ]),
  trendRow('cf-2022', '2022/03/31', [
    cell('30,480,967.00', '(+10.81)'),
    cell('-728,780.00', '(-29.24)'),
    cell('-336,578.00', '(+0.58)'),
    cell('11,382.57', '(+23.54)'),
    cell('2022/05/28'),
  ]),
  trendRow('cf-2023', '2023/03/31', [
    cell('31,154,095.00', '(+2.21)'),
    cell('-1,052,664.00', '(-44.44)'),
    cell('84,300.00', '(+125.05)'),
    cell('3,268.50', '(+20.41)'),
    cell('2023/05/28'),
  ]),
  trendRow('cf-2024', '2024/03/31', [
    cell('34,107,490.00', '(+9.48)'),
    cell('-900,000.00', '(+22.21)'),
    cell('-210,709.00', '(-349.95)'),
    cell('14,811.92', '(+5.13)'),
    cell('2024/05/28'),
  ]),
  trendRow('cf-2025', '2025/03/31', [
    cell('35,293,173.00', '(+3.48)'),
    cell('-930,120.00', '(-13.58)'),
    cell('-298,243.00', '(-41.54)'),
    cell('26,379.67', '(+0.70)'),
    cell('2025/05/28'),
  ]),
];

// ---- Research & Development ----------------------------------------
// Node 3338:33250. Rows/columns are built from the API response by
// `mapResearchDevelopmentApiToFinancialSection`, so no mock financial values
// live here.

// ---- Chart data (representative; Figma chart data is placeholder) ----
const annualCharts = (prefix: string) => {
  const labels = ['16/03', '17/03', '18/03', '19/03', '20/03', '21/03', '22/03', '23/03', '24/03', '25/03予'];
  const mk = (
    key: string,
    title: string,
    max: number,
    interval: number,
    values: number[]
  ) => ({
    key: `${prefix}-${key}`,
    title,
    unit: '百万円',
    max,
    interval,
    points: labels.map((label, i) => ({ label, value: values[i] })),
  });
  return [
    mk('revenue', '売上高(百万円)', 15000000, 5000000, [8998661, 9210000, 9560000, 9700000, 9800000, 9000000, 9921513, 10974373, 13020768, 12957064]),
    mk('operating', '営業利益(百万円)', 2000000, 500000, [956958, 1010000, 1060000, 1080000, 1090000, 1000000, 1112396, 1273205, 1166986, 1412640]),
    mk('ordinary', '経常利益(百万円)', 2000000, 500000, [997965, 1050000, 1090000, 1100000, 1110000, 1020000, 1117503, 1274496, 1268662, 1473726]),
    mk('net', '当期利益(百万円)', 1500000, 500000, [1043896, 930000, 870000, 900000, 910000, 820000, 888406, 1011773, 980494, 1159887]),
  ];
};

const CF_LABELS = ['2021', '2022', '2023', '2024', '2025'];

// Balance sheet is API-backed (GET /api/fundamentals/balance-sheet); only the
// series identity/colours are static. Labels, values and the axis scale come
// from `mapBalanceSheetApiToFinancialSection`, never from here.
// 負債合計 is intentionally absent: the response has no total-liabilities field.
const balanceSeries = [
  { key: 'total-assets', label: '総資産', color: FINANCIAL_COLORS.red, values: [] },
  { key: 'long-term-debt', label: '固定負債', color: FINANCIAL_COLORS.blue, values: [] },
];

const cashFlowSeries = [
  { key: 'operating-cf', label: '営業CF', color: FINANCIAL_COLORS.red, values: [1400000, 1500000, 1300000, 1600000, 1800000] },
  { key: 'investing-cf', label: '投資CF', color: FINANCIAL_COLORS.blue, values: [-563910, -728780, -1052664, -900000, -930120] },
  { key: 'financing-cf', label: '財務CF', color: FINANCIAL_COLORS.purple, values: [-338533, -336578, 84300, -210709, -298243] },
];

const rdSeries = [
  { key: 'depreciation', label: '減価償却（含むのれん償却）', color: FINANCIAL_COLORS.red, values: [] },
  { key: 'capex', label: '設備投資', color: FINANCIAL_COLORS.blue, values: [] },
  { key: 'research', label: '研究開発費', color: FINANCIAL_COLORS.purple, values: [] },
];

// ---- Assembled screen data -----------------------------------------
export const mockPerformanceTrends: PerformanceTrendsData = {
  quickLinks: [
    { key: 'annual', label: '通期業績 ▼', href: '#annual', nodeId: '3158:5214' },
    { key: 'quarterly', label: '四半期予想 ▼', href: '#quarterly', nodeId: '3158:5215' },
    { key: 'balance', label: '貸借対照表 ▼', href: '#balance', nodeId: '3158:5216' },
    { key: 'cashflow', label: 'キャッシュフロー ▼', href: '#cashflow', nodeId: '3158:5217' },
  ],

  annual: {
    title: '通期業績推移',
    headerNodeId: '3131:2528',
    sectionNodeId: '3131:2159',
    controls: annualControls(),
    charts: annualCharts('annual'),
    note: ANNUAL_NOTE,
    table: {
      nodeId: '3350:25858',
      labelColumnWidth: UNIT_LABEL_COLUMN_WIDTH,
      // Mobile node 3350:27191 shows a 342px viewport, but its fixed cells are
      // 104px + 6 data columns. work_items_86 keeps two decimals on large
      // amounts, so each value column needs 102px for strings such as
      // `12,016,563.00`; the 716px table scrolls inside narrow wrappers.
      minWidth: 716,
      columns: [
        { key: 'label', label: [L('経常利益推移')], align: 'left' },
        { key: 'revenue', label: [L('売上高')], width: 102, align: 'right' },
        { key: 'operating', label: [L('営業利益')], width: 102, align: 'right' },
        { key: 'ordinary', label: [L('経常利益')], width: 102, align: 'right' },
        { key: 'net', label: [L('当期利益')], width: 102, align: 'right' },
        { key: 'eps', label: [L('EPS')], width: 102, align: 'right' },
        { key: 'bps', label: [L('BPS')], width: 102, align: 'right' },
      ],
      rows: annualRows,
    },
  },

  quarterly: {
    title: '四半期業績推移',
    headerNodeId: '3132:2535',
    sectionNodeId: '3132:2534',
    controls: quarterlyControls(),
    charts: [
      { key: 'quarterly-revenue', title: '売上高', unit: '', yAxisMin: 0, yAxisMax: 100, yAxisInterval: 25, points: [] },
      { key: 'quarterly-operating', title: '営業利益', unit: '', yAxisMin: 0, yAxisMax: 100, yAxisInterval: 25, points: [] },
      { key: 'quarterly-pretax', title: '税引前利益', unit: '', yAxisMin: 0, yAxisMax: 100, yAxisInterval: 25, points: [] },
      { key: 'quarterly-net', title: '最終利益', unit: '', yAxisMin: 0, yAxisMax: 100, yAxisInterval: 25, points: [] },
    ],
    note: QUARTERLY_NOTE,
    table: {
      nodeId: '3350:25521',
      labelColumnWidth: UNIT_LABEL_COLUMN_WIDTH,
      minWidth: 850,
      columns: [
        { key: 'label', label: [L('決算期')], align: 'left' },
        { key: 'revenue', label: [L('売上高')], align: 'right' },
        { key: 'operating', label: [L('営業利益')], align: 'right' },
        { key: 'pretax', label: [L('税引前利益')], align: 'right' },
        { key: 'net', label: [L('最終利益')], align: 'right' },
        { key: 'eps', label: [L('EPS')], align: 'right' },
        { key: 'bps', label: [L('BPS')], align: 'right' },
      ],
      rows: [],
    },
  },

  balanceSheet: {
    title: '貸借対照表',
    headerNodeId: '3157:7984',
    sectionNodeId: '3157:7983',
    legend: balanceSeries,
    // Presentation metadata only. `columns`/`rows`/`labels` and the axis bounds
    // are replaced by mapBalanceSheetApiToFinancialSection at render time.
    chart: {
      key: 'balance',
      labels: [],
      yAxisMin: 0,
      yAxisMax: 0,
      yAxisInterval: 0,
      series: balanceSeries,
    },
    table: {
      nodeId: '3349:24052',
      labelColumnWidth: UNIT_LABEL_COLUMN_WIDTH,
      minWidth: 1180,
      columns: [],
      rows: [],
    },
  },

  cashFlow: {
    title: 'キャッシュフロー',
    headerNodeId: '3157:8727',
    sectionNodeId: '3490:22285',
    legend: cashFlowSeries,
    chart: {
      key: 'cashflow',
      labels: [],
      yAxisMin: 0,
      yAxisMax: 0,
      yAxisInterval: 0,
      series: cashFlowSeries.map((series) => ({ ...series, values: [] })),
    },
    table: {
      nodeId: '3347:21870',
      labelColumnWidth: UNIT_LABEL_COLUMN_WIDTH,
      minWidth: 940,
      columns: [
        { key: 'label', label: [L('単位:'), L('百万円')], align: 'left' },
        { key: 'cashEquivalents', label: [L('現金等期末残高')], align: 'right' },
        { key: 'operatingCf', label: [L('営業CF')], align: 'right' },
        { key: 'investmentCf', label: [L('投資CF')], align: 'right' },
        { key: 'financingCf', label: [L('財務CF')], align: 'right' },
        { key: 'freeCashFlow', label: [L('フリーCF')], align: 'right' },
        { key: 'perShareOperatingCf', label: [L('一株営業CF')], align: 'right' },
        { key: 'announceDate', label: [L('決算発表日')], align: 'right' },
      ],
      rows: [],
    },
  },

  researchDevelopment: {
    title: '研究開発設備投資',
    headerNodeId: '3226:14930',
    sectionNodeId: '3490:22286',
    legend: rdSeries,
    chart: {
      key: 'rd',
      labels: [],
      yAxisMin: 0,
      yAxisMax: 0,
      yAxisInterval: 0,
      series: rdSeries.map((series) => ({ ...series, values: [] })),
    },
    table: {
      nodeId: '3338:33250',
      labelColumnWidth: 110,
      // `minWidth` is response-dependent and computed by
      // `mapResearchDevelopmentApiToFinancialSection`
      // (labelColumnWidth + periodCount * period-column width).
      columns: [],
      rows: [],
    },
  },
};

export default mockPerformanceTrends;
